import React, { useState, useEffect } from "react";
import { database } from "../../firebase";
import { ref, onValue, query, orderByChild } from "firebase/database";
import { useAuth } from "../../components/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaCalendarAlt,
  FaUserCircle,
  FaPlus,
  FaComment,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
} from "react-icons/fa";

const statusColors = {
  접수: "bg-yellow-100 text-yellow-800 border-yellow-200",
  처리중: "bg-blue-100 text-blue-800 border-blue-200",
  완료: "bg-green-100 text-green-800 border-green-200",
};

const SupportBoard = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const { user, isAdmin } = useAuth();
  const adminStatus = isAdmin; // isAdmin은 이제 boolean 값입니다.
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [userDomain, setUserDomain] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("all"); // 관리자용 도메인 필터
  const [domains, setDomains] = useState([]); // 도메인 목록

  const theme = {
    primary: "bg-indigo-600",
    secondary: "bg-indigo-50",
    hover: "hover:bg-indigo-700",
    text: "text-indigo-600",
    border: "border-indigo-300",
    light: "bg-indigo-50",
  };

  // 사용자 도메인 추출
  useEffect(() => {
    if (user && user.email) {
      const domain = user.email.split("@")[1];
      setUserDomain(domain);
    }
  }, [user]);

  // Firebase에서 데이터 읽기
  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: "/support" } });
      return;
    }

    setIsLoading(true);
    const ticketsRef = ref(database, "supportTickets");

    onValue(query(ticketsRef, orderByChild("createdAt")), (snapshot) => {
      const data = snapshot.val();
      const ticketList = data
        ? Object.entries(data)
            .reverse()
            .map(([id, value]) => ({ id, ...value }))
        : [];

      setTickets(ticketList);
      
      // 도메인 목록 추출 (관리자용)
      if (adminStatus) {
        const uniqueDomains = [...new Set(ticketList.map(ticket => ticket.author.split("@")[1]))];
        setDomains(uniqueDomains);
      }
      
      // 사용자 권한에 따른 자동 필터링 적용
      applyDomainFilter(ticketList);
      setIsLoading(false);
    });
  }, [user, navigate, userDomain, adminStatus]);

  // 도메인 필터 적용 (관리자는 모든 티켓, 일반 사용자는 자신의 도메인 티켓만)
  const applyDomainFilter = (ticketList) => {
    let filtered = ticketList;
    
    if (adminStatus) {
      // 관리자: 선택된 도메인으로 필터링
      if (selectedDomain !== "all") {
        filtered = ticketList.filter((ticket) => {
          const ticketDomain = ticket.author.split("@")[1];
          return ticketDomain === selectedDomain;
        });
      }
    } else {
      // 일반 사용자: 자신의 도메인 티켓만
      filtered = ticketList.filter((ticket) => {
        const ticketDomain = ticket.author.split("@")[1];
        return ticketDomain === userDomain;
      });
    }
    
    setFilteredTickets(filtered);
    setCurrentPage(1);
  };

  // 도메인 변경 시 필터 적용
  useEffect(() => {
    applyDomainFilter(tickets);
  }, [selectedDomain, tickets, adminStatus, userDomain]);

  // 페이지네이션 계산 로직
  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(
    indexOfFirstTicket,
    indexOfLastTicket
  );
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

  // 페이지 변경 함수
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // 이전/다음 페이지 함수
  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // 페이지 범위 계산 (최대 5개 페이지만 표시)
  const getPageRange = () => {
    const totalPageCount = Math.ceil(filteredTickets.length / ticketsPerPage);
    const pageWindowSize = 5;
    let startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    let endPage = startPage + pageWindowSize - 1;

    if (endPage > totalPageCount) {
      endPage = totalPageCount;
      startPage = Math.max(1, endPage - pageWindowSize + 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 섹션 */}
        <div className="mb-10 bg-white rounded-2xl shadow-md p-6 sm:p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div
                className={`inline-flex items-center ${theme.secondary} ${theme.text} text-sm font-medium px-3 py-1 rounded-full mb-2`}
              >
                고객지원
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                고객 지원 센터
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                문의사항이나 지원 요청을 등록해주세요. 최대한 빠르게
                도와드리겠습니다.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* 사용자 도메인 정보 배지 */}
              {!isAdmin && (
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">
                  <FaBuilding className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">내 도메인 문의</span>
                  <span className="sm:hidden">도메인</span>
                  <span className="ml-2 py-0.5 px-1.5 text-xs rounded-full bg-indigo-100 border border-indigo-200">
                    @{userDomain}
                  </span>
                </div>
              )}

              {/* 문의하기 버튼 */}
              <button
                className="inline-flex items-center px-4 py-2.5 rounded-lg shadow-md text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => navigate("/support/new")}
              >
                <FaPlus className="w-4 h-4 mr-2" />
                문의하기
              </button>
            </div>
          </div>
        </div>

        {/* 상태 정보 표시 */}
        <div className="mb-5 bg-white rounded-xl shadow-sm p-3 border border-gray-100 flex flex-wrap justify-between items-center">
          <div className="flex items-center space-x-2">
            {adminStatus && (
              <>
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-medium shadow-sm">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  관리자 모드 (전체 문의 조회)
                </span>
                {/* 도메인 필터 드롭다운 */}
                <select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  className="ml-4 px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">모든 도메인</option>
                  {domains.map((domain) => (
                    <option key={domain} value={domain}>
                      @{domain}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="mt-2 sm:mt-0">
            {filteredTickets.length > 0 && (
              <div className="inline-flex items-center bg-gray-100 px-3 py-1 rounded-lg text-gray-700 text-sm font-medium">
                <svg
                  className="w-4 h-4 mr-1 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <span>
                  총{" "}
                  <span className="font-bold text-indigo-600">
                    {filteredTickets.length}
                  </span>
                  건의 문의
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* 게시물 리스트 */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
          {isLoading ? (
            <div className="flex justify-center items-center p-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="text-lg font-medium">
                {!adminStatus
                  ? "해당 도메인에 등록된 문의가 없습니다."
                  : "등록된 문의가 없습니다."}
              </p>
              <p className="mt-1 text-sm">
                첫 번째 문의를 작성해 보세요!
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${theme.secondary}`}>
                  <tr>
                    <th
                      scope="col"
                      className="px-3 sm:px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 sm:w-16"
                    >
                      번호
                    </th>
                    <th
                      scope="col"
                      className="px-3 sm:px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      제목
                    </th>
                    <th
                      scope="col"
                      className="px-3 sm:px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-24"
                    >
                      상태
                    </th>
                    <th
                      scope="col"
                      className="hidden sm:table-cell px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                    >
                      작성자
                    </th>
                     <th
                      scope="col"
                      className="hidden sm:table-cell px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                    >
                      도메인
                    </th>
                    <th
                      scope="col"
                      className="hidden sm:table-cell px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                    >
                      작성일
                    </th>
                    <th
                      scope="col"
                      className="hidden sm:table-cell px-6 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20"
                    >
                      댓글
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {currentTickets.map((ticket, index) => (
                    <tr
                      key={ticket.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center justify-center bg-gray-100 rounded-full h-6 w-6 sm:h-7 sm:w-7">
                          {filteredTickets.length -
                            (indexOfFirstTicket + index)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <button
                            onClick={() => navigate(`/support/${ticket.id}`)}
                            className={`text-gray-800 ${theme.text} hover:underline font-medium transition-colors duration-200 text-left text-sm sm:text-base`}
                          >
                            {ticket.title}
                          </button>
                          <div className="flex items-center mt-1 sm:hidden text-xs text-gray-500">
                            <FaUserCircle className="mr-1" />
                            <span>
                              {ticket.authorName || ticket.author.split("@")[0]}{" "}
                              ·
                            </span>
                            <FaCalendarAlt className="ml-2 mr-1" />
                            <span>{ticket.createdAt}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            statusColors[ticket.status] ||
                            "bg-gray-100 text-gray-800 border-gray-200"
                          }`}
                        >
                          {ticket.status || "접수"}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaUserCircle className="mr-2 text-gray-400" />
                          <span>
                            {ticket.authorName ? (
                              <span className="font-medium">
                                {ticket.authorName}
                              </span>
                            ) : (
                              <span className="font-medium">
                                {ticket.author}
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                       <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          {ticket.author.split("@")[1]}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaCalendarAlt className="mr-2 text-gray-400" />
                          {ticket.createdAt}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaComment className="mr-2 text-gray-400" />
                          {ticket.commentCount || 0}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 페이지네이션 */}
        {filteredTickets.length > 0 && (
          <div className="mt-8 flex justify-center">
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-l-md border ${
                  theme.border
                } bg-white text-sm font-medium ${
                  currentPage === 1
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:bg-gray-50"
                } transition-colors duration-150`}
              >
                <FaChevronLeft className="w-4 h-4" />
              </button>

              {getPageRange().map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => paginate(pageNumber)}
                  className={`relative inline-flex items-center px-4 py-2 border ${
                    theme.border
                  } text-sm font-medium ${
                    currentPage === pageNumber
                      ? `z-10 ${theme.primary} text-white`
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  } transition-colors duration-150`}
                >
                  {pageNumber}
                </button>
              ))}

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-r-md border ${
                  theme.border
                } bg-white text-sm font-medium ${
                  currentPage === totalPages
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500 hover:bg-gray-50"
                } transition-colors duration-150`}
              >
                <FaChevronRight className="w-4 h-4" />
              </button>
            </nav>
          </div>
        )}
      </div>
    </section>
  );
};

export default SupportBoard;